import React, { useEffect, useState } from 'react';
import { Send, FileText, Mail, Building2, MapPin, User, Heart, Briefcase, Check, AlertCircle, Paperclip, Search, X, Plus } from 'lucide-react';
import { sapi } from '../../lib/api';
import { Spinner, useToast, Toast } from '../../components/UI';
import { useLocation } from 'react-router-dom';

interface CV { id: string; name: string; category: string; fileName: string; }
interface CoverLetter { id: string; name: string; body: string; }
interface EmailBody { id: string; name: string; subject: string; body: string; }
interface Company { id: string; name: string; email: string; address: string; managerName: string; }
interface Admiration { id: string; text: string; }
interface JobTitleOpt { id: string; text: string; }

const ADMIRE_KEY = 'leadflow.includeAdmire';

// Keep these two in step with joinAdmirables / stripAdmirables in the server's
// studentRoutes.js - the server builds the real email + PDF, this is the preview.
function joinAdmirables(list: string[]): string {
  const l = list.map(s => s.trim().replace(/[.\s]+$/, '')).filter(Boolean);
  if (l.length <= 1) return l[0] || '';
  return `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}`;
}

function stripAdmirables(text: string): string {
  const PH = '{{admirables}}';
  let guard = 0;
  while (text.includes(PH) && guard++ < 20) {
    const i = text.indexOf(PH);
    let s = i; while (s > 0 && !/[.!?\n]/.test(text[s - 1])) s--;
    let e = i + PH.length; while (e < text.length && !/[.!?\n]/.test(text[e])) e++;
    const before = text.slice(s, i), after = text.slice(i + PH.length, e);
    const lead = (before.match(/^\s*/) || [''])[0];
    const cont = after.match(/^\s*(,\s*(and|but|so)\s+|,\s*|;\s*|\s+and\s+)(?=\S)/i);
    if (cont && after.slice(cont[0].length).trim()) {
      let rest = after.slice(cont[0].length);
      rest = rest.charAt(0).toUpperCase() + rest.slice(1);
      text = text.slice(0, s) + lead + rest + text.slice(e);
    } else {
      const cut = Math.max(before.lastIndexOf(','), before.lastIndexOf(' and '), before.lastIndexOf(';'));
      if (cut > 0 && !after.trim()) {
        text = text.slice(0, s + cut) + text.slice(e);
      } else {
        let end = e < text.length && /[.!?]/.test(text[e]) ? e + 1 : e;
        while (end < text.length && text[end] === ' ') end++;
        text = text.slice(0, s) + (s > 0 && text[s - 1] !== '\n' ? ' ' : '') + text.slice(end).replace(/^\s+/, m => m.includes('\n') ? m : '');
        text = text.replace(/ {2,}/g, ' ');
      }
    }
  }
  return text;
}

function replacePH(text: string, vars: Record<string, string>): string {
  if (!vars.admirables) text = stripAdmirables(text);
  return text
    .replace(/\{\{company_name\}\}/g,  vars.companyName  || '')
    .replace(/\{\{address\}\}/g,       vars.address      || '')
    .replace(/\{\{manager_name\}\}/g,  vars.managerName  || 'Hiring Manager')
    .replace(/\{\{admirables\}\}/g,    vars.admirables   || '')
    .replace(/\{\{job_title\}\}/g,     vars.jobTitle     || '')
    .replace(/\{\{your_name\}\}/g,     vars.yourName     || '')
    .replace(/\{\{date\}\}/g, new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
}

export default function ApplyPage(): React.ReactElement {
  const { toast, show } = useToast();
  const location = useLocation();
  const [cvs, setCvs] = useState<CV[]>([]);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [emailBodies, setEmailBodies] = useState<EmailBody[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [selectedCV, setSelectedCV] = useState('');
  const [selectedCL, setSelectedCL] = useState('');
  const [selectedEB, setSelectedEB] = useState('');
  const [form, setForm] = useState({ companyEmail: '', companyName: '', address: '', managerName: '', jobTitle: '' });

  // "What you admire" - optional, several can be picked, and each is saved for reuse
  const [admirations, setAdmirations] = useState<Admiration[]>([]);
  const [selectedAdm, setSelectedAdm] = useState<string[]>([]);
  const [newAdm, setNewAdm] = useState('');
  const [savingAdm, setSavingAdm] = useState(false);
  const [includeAdmire, setIncludeAdmire] = useState<boolean>(() => {
    try { return localStorage.getItem(ADMIRE_KEY) !== 'off'; } catch { return true; }
  });
  const toggleAdmire = () => setIncludeAdmire(v => {
    try { localStorage.setItem(ADMIRE_KEY, v ? 'off' : 'on'); } catch { /* private mode */ }
    return !v;
  });

  // Job Title - picked the same way as "what you admire", but single-valued:
  // tap a saved one to reuse it, or type one and save it for next time.
  const [jobTitles, setJobTitles] = useState<JobTitleOpt[]>([]);
  const [savingJobTitle, setSavingJobTitle] = useState(false);

  useEffect(() => {
    Promise.all([
      sapi.getCVs(), sapi.getCoverLetters(), sapi.getEmailBodies(), sapi.getCompanies(),
      sapi.getAdmirations().catch(() => []), sapi.getJobTitles().catch(() => []),
    ])
      .then(([c, cl, eb, co, ad, jt]) => { setCvs(c); setCoverLetters(cl); setEmailBodies(eb); setCompanies(co); setAdmirations(ad); setJobTitles(jt); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Pre-fill from Companies page "Apply Again", Find Jobs "Draft Application",
  // or AI Application's draft - all three navigate here with this same shape.
  useEffect(() => {
    const state = location.state as { company?: Company; jobTitle?: string; cvId?: string; coverLetterId?: string; emailBodyId?: string } | null;
    if (state?.company) {
      const c = state.company;
      setForm(p => ({ ...p, companyEmail: c.email, companyName: c.name, address: c.address || '', managerName: c.managerName || '', jobTitle: state.jobTitle || p.jobTitle }));
      // Only show a name here, never fall back to the email - this box doubles as a
      // "saved company" search field, and an email sitting in it reads as a stray value.
      setCompanySearch(c.name || '');
    }
    if (state?.cvId) setSelectedCV(state.cvId);
    if (state?.coverLetterId) setSelectedCL(state.coverLetterId);
    if (state?.emailBodyId) setSelectedEB(state.emailBodyId);
  }, [location.state]);

  const fillFromCompany = (c: Company) => {
    setForm(p => ({ ...p, companyEmail: c.email, companyName: c.name, address: c.address || '', managerName: c.managerName || '' }));
    setCompanySearch(c.name || c.email);
    setShowSuggestions(false);
  };

  const suggestions = companySearch.length > 0
    ? companies.filter(c => c.name.toLowerCase().includes(companySearch.toLowerCase()) || c.email.toLowerCase().includes(companySearch.toLowerCase()))
    : [];

  const admireTexts = selectedAdm.map(id => admirations.find(a => a.id === id)?.text).filter((t): t is string => !!t);
  const admireOn = includeAdmire && admireTexts.length > 0;

  const toggleAdm = (id: string) =>
    setSelectedAdm(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const addAdmiration = async () => {
    const text = newAdm.trim();
    if (!text) return;
    const existing = admirations.find(a => a.text.toLowerCase() === text.toLowerCase());
    if (existing) {
      if (!selectedAdm.includes(existing.id)) setSelectedAdm(p => [...p, existing.id]);
      setNewAdm('');
      return;
    }
    setSavingAdm(true);
    try {
      const a = await sapi.createAdmiration(text);
      setAdmirations(p => [a, ...p]);
      setSelectedAdm(p => [...p, a.id]);
      setNewAdm('');
    } catch (e: any) { show(e.message || 'Could not save', 'error'); }
    finally { setSavingAdm(false); }
  };

  const removeAdmiration = async (id: string) => {
    if (!window.confirm('Delete this saved admiration?')) return;
    try {
      await sapi.deleteAdmiration(id);
      setAdmirations(p => p.filter(a => a.id !== id));
      setSelectedAdm(p => p.filter(x => x !== id));
    } catch (e: any) { show(e.message || 'Could not delete', 'error'); }
  };

  const saveJobTitle = async () => {
    const text = form.jobTitle.trim();
    if (!text) return;
    if (jobTitles.some(j => j.text.toLowerCase() === text.toLowerCase())) return;
    setSavingJobTitle(true);
    try {
      const j = await sapi.createJobTitle(text);
      setJobTitles(p => [j, ...p]);
    } catch (e: any) { show(e.message || 'Could not save', 'error'); }
    finally { setSavingJobTitle(false); }
  };

  const removeJobTitle = async (id: string) => {
    if (!window.confirm('Delete this saved job title?')) return;
    try {
      await sapi.deleteJobTitle(id);
      setJobTitles(p => p.filter(j => j.id !== id));
    } catch (e: any) { show(e.message || 'Could not delete', 'error'); }
  };

  const cv = cvs.find(c => c.id === selectedCV) ?? null;
  const cl = coverLetters.find(c => c.id === selectedCL) ?? null;
  const eb = emailBodies.find(e => e.id === selectedEB) ?? null;
  const vars = { companyName: form.companyName, address: form.address, managerName: form.managerName, jobTitle: form.jobTitle, admirables: admireOn ? joinAdmirables(admireTexts) : '', yourName: '' };
  const previewSubject = eb ? replacePH(eb.subject, vars) : '';
  const previewBody    = eb ? replacePH(eb.body, vars) : '';
  const previewReady = !!selectedCV && !!selectedCL && !!selectedEB;
  const ready = previewReady && !!form.companyEmail.trim() && !!form.companyName.trim();

  const fld = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  const apply = async () => {
    if (!ready) return;
    setSending(true); setSent(false);
    try {
      await sapi.apply({ cvId: selectedCV, coverLetterId: selectedCL, emailBodyId: selectedEB, ...form, admirables: admireOn ? admireTexts : [] });
      setSent(true);
      show(`Application sent to ${form.companyEmail}`, 'success');

      // In case it wasn't saved explicitly before sending, keep it for next time.
      saveJobTitle();

      setForm({ companyEmail: '', companyName: '', address: '', managerName: '', jobTitle: '' });
      setSelectedAdm([]);
      setSelectedCV(''); setSelectedCL(''); setSelectedEB('');
      setTimeout(() => setSent(false), 4000);
    } catch (e: any) { show(e.message || 'Failed to send application', 'error'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}><Spinner size={28} /></div>;

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div className="page-header">
        <div><h1 className="page-title">Apply for Job</h1><p className="page-sub">Fill in company details, pick your documents, send in one click</p></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {/* Left: config */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Company Details */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>1</div>
              Company Details
            </div>

            {/* Saved company search */}
            {companies.length > 0 && (
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Search saved companies</div>
                <div className="search-wrap">
                  <Search size={14} />
                  <input
                    placeholder="Type company name or email…"
                    value={companySearch}
                    onChange={e => { setCompanySearch(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  />
                  {companySearch && <button onClick={() => { setCompanySearch(''); setShowSuggestions(false); }} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}><X size={13} /></button>}
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', marginTop: 4, overflow: 'hidden' }}>
                    {suggestions.slice(0, 6).map(c => (
                      <div key={c.id} onMouseDown={() => fillFromCompany(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{(c.name || c.email).charAt(0).toUpperCase()}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || '(unnamed)'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>Applied {(c as any).timesApplied}×</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label><Mail size={10} style={{ marginRight: 4 }} />Company Email *</label>
              <input type="email" placeholder="hr@company.com" value={form.companyEmail} onChange={fld('companyEmail')} />
            </div>
            <div className="form-group">
              <label><Building2 size={10} style={{ marginRight: 4 }} />Company Name *</label>
              <input placeholder="Acme Corp" value={form.companyName} onChange={fld('companyName')} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label><MapPin size={10} style={{ marginRight: 4 }} />Address</label>
                <input placeholder="123 Main St, Harare" value={form.address} onChange={fld('address')} />
              </div>
              <div className="form-group">
                <label><User size={10} style={{ marginRight: 4 }} />Manager Name</label>
                <input placeholder="Mr. John Smith — blank reads as “Hiring Manager”" value={form.managerName} onChange={fld('managerName')} />
              </div>
            </div>

            <div className="form-group">
              <label><Briefcase size={10} style={{ marginRight: 4 }} />Job Title</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Software Developer" value={form.jobTitle} onChange={fld('jobTitle')}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveJobTitle(); } }} style={{ flex: 1 }} />
                <button type="button" onClick={saveJobTitle} disabled={!form.jobTitle.trim() || savingJobTitle}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 8, padding: '0 14px', fontSize: 12, fontWeight: 600, cursor: form.jobTitle.trim() ? 'pointer' : 'not-allowed', opacity: form.jobTitle.trim() ? 1 : 0.5 }}>
                  {savingJobTitle ? <Spinner size={12} /> : <Plus size={13} />} Save
                </button>
              </div>
              {jobTitles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {jobTitles.map(jt => {
                    const on = form.jobTitle === jt.text;
                    return (
                      <span key={jt.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', padding: '5px 6px 5px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', border: `1px solid ${on ? 'var(--accent)' : 'var(--border2)'}`, background: on ? 'rgba(108,99,255,0.12)' : 'transparent', color: on ? 'var(--text)' : 'var(--text2)' }}
                        onClick={() => setForm(p => ({ ...p, jobTitle: jt.text }))} title={on ? 'Currently used' : 'Click to use'}>
                        {on && <Check size={11} color="var(--accent)" />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jt.text}</span>
                        <button type="button" aria-label="Delete saved job title" onClick={ev => { ev.stopPropagation(); removeJobTitle(jt.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 2 }}><X size={11} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ marginBottom: 0 }}><Heart size={10} style={{ marginRight: 4 }} />What you admire about them</label>
                <button type="button" onClick={toggleAdmire} role="switch" aria-checked={includeAdmire} title={includeAdmire ? 'Included in the letter - click to leave it out' : 'Left out - click to include'}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: includeAdmire ? 'var(--accent)' : 'var(--text3)', fontSize: 11, fontWeight: 600, padding: 0 }}>
                  {includeAdmire ? 'Include' : 'Off'}
                  <span style={{ width: 32, height: 18, borderRadius: 9, background: includeAdmire ? 'var(--accent)' : 'var(--border2)', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: includeAdmire ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                  </span>
                </button>
              </div>

              {!includeAdmire ? (
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                  Left out. The sentence in your templates that mentions it is removed automatically.
                </div>
              ) : (
                <>
                  {admirations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {admirations.map(a => {
                        const on = selectedAdm.includes(a.id);
                        return (
                          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', padding: '5px 6px 5px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', border: `1px solid ${on ? 'var(--accent)' : 'var(--border2)'}`, background: on ? 'rgba(108,99,255,0.12)' : 'transparent', color: on ? 'var(--text)' : 'var(--text2)' }}
                            onClick={() => toggleAdm(a.id)} title={on ? 'Selected - click to unselect' : 'Click to use'}>
                            {on && <Check size={11} color="var(--accent)" />}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.text}</span>
                            <button type="button" aria-label="Delete saved admiration" onClick={ev => { ev.stopPropagation(); removeAdmiration(a.id); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 2 }}><X size={11} /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input placeholder="e.g. their commitment to innovation" value={newAdm} onChange={e => setNewAdm(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAdmiration(); } }} style={{ flex: 1 }} />
                    <button type="button" onClick={addAdmiration} disabled={!newAdm.trim() || savingAdm}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 8, padding: '0 14px', fontSize: 12, fontWeight: 600, cursor: newAdm.trim() ? 'pointer' : 'not-allowed', opacity: newAdm.trim() ? 1 : 0.5 }}>
                      {savingAdm ? <Spinner size={12} /> : <Plus size={13} />} Add
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
                    {admireTexts.length === 0
                      ? (admirations.length ? 'Tap one or more to use them. ' : '') + 'New ones are saved for next time. If none are selected, the sentence is left out.'
                      : <>Reads as: <span style={{ color: 'var(--text2)' }}>{joinAdmirables(admireTexts)}</span></>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CV picker */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>2</div>
              Choose CV
            </div>
            {cvs.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No CVs — upload one in the CVs page first</div>
            : cvs.map(c => {
              const active = selectedCV === c.id;
              return (
                <div key={c.id} onClick={() => setSelectedCV(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'rgba(108,99,255,0.08)' : 'transparent', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 4 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FileText size={14} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.category} · {c.fileName}</div>
                  </div>
                  {active && <Check size={13} color="var(--accent)" />}
                </div>
              );
            })}
          </div>

          {/* Cover Letter picker */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>3</div>
              Choose Cover Letter Template
            </div>
            {coverLetters.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No cover letters — create one first</div>
            : coverLetters.map(c => {
              const active = selectedCL === c.id;
              return (
                <div key={c.id} onClick={() => setSelectedCL(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'rgba(108,99,255,0.08)' : 'transparent', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 4 }}>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                  {active && <Check size={13} color="var(--accent)" />}
                </div>
              );
            })}
          </div>

          {/* Email Body picker */}
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>4</div>
              Choose Email Body
            </div>
            {emailBodies.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No email bodies — create one first</div>
            : emailBodies.map(e => {
              const active = selectedEB === e.id;
              return (
                <div key={e.id} onClick={() => setSelectedEB(e.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'rgba(108,99,255,0.08)' : 'transparent', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{e.name}</div>
                    {e.subject && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</div>}
                  </div>
                  {active && <Check size={13} color="var(--accent)" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Preview + Send */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Preview */}
          <div className="card" style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Email Preview</div>
            {!previewReady ? (
              <div className="empty-state" style={{ padding: '40px 0' }}><Send size={28} /><p>Select a CV, cover letter and email body to preview</p></div>
            ) : (
              <>
                {/* To + Subject */}
                <div style={{ background: 'var(--surface2)', borderRadius: 9, padding: '12px 14px', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 12, display: 'flex', gap: 8 }}><span style={{ color: 'var(--text3)', minWidth: 52 }}>To:</span><span style={{ fontWeight: 600, color: form.companyEmail ? 'inherit' : 'var(--text3)' }}>{form.companyEmail || 'add company email →'}</span></div>
                  {previewSubject && <div style={{ fontSize: 12, display: 'flex', gap: 8 }}><span style={{ color: 'var(--text3)', minWidth: 52 }}>Subject:</span><span style={{ fontWeight: 600 }}>{previewSubject}</span></div>}
                </div>

                {/* Email body preview */}
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Email Body</div>
                <div className="preview-box" style={{ whiteSpace: 'pre-wrap', minHeight: 100, fontSize: 13 }}>{previewBody}</div>

                {/* Attachments */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Attachments</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cv && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8, fontSize: 12 }}>
                        <Paperclip size={12} color="var(--text3)" />
                        <span style={{ fontWeight: 500 }}>{cv.fileName}</span>
                        <span style={{ color: 'var(--text3)', marginLeft: 'auto' }}>Your CV</span>
                      </div>
                    )}
                    {cl && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8, fontSize: 12 }}>
                        <Paperclip size={12} color="var(--text3)" />
                        <span style={{ fontWeight: 500 }}>Cover Letter — {form.companyName || 'Application'}.pdf</span>
                        <span style={{ color: 'var(--text3)', marginLeft: 'auto' }}>Generated PDF</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Send Button */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!ready && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text3)' }}>
                <AlertCircle size={13} />
                <div style={{ lineHeight: 1.5 }}>
                  {!form.companyEmail && <div>• Company email required</div>}
                  {!form.companyName && <div>• Company name required</div>}
                  {!selectedCV && <div>• Select a CV</div>}
                  {!selectedCL && <div>• Select a cover letter</div>}
                  {!selectedEB && <div>• Select an email body</div>}
                </div>
              </div>
            )}
            <button
              onClick={apply}
              disabled={!ready || sending}
              style={{ background: sent ? 'var(--accent3)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 24px', fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, cursor: (!ready || sending) ? 'not-allowed' : 'pointer', opacity: (!ready || sending) ? 0.5 : 1, transition: 'all 0.2s', boxShadow: ready ? '0 4px 24px rgba(108,99,255,0.3)' : 'none' }}
            >
              {sending ? <><Spinner size={16} /> Sending application…</>
              : sent ? <><Check size={16} /> Application Sent!</>
              : <><Send size={16} /> Send Application</>}
            </button>
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
              Sends email with your CV and a generated cover letter PDF attached
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
